import type { TerminalAttachment } from '@this+that/models';

import type { TerminalAttachmentUpload } from './types';
import { resolveContentType } from '../utils/terminalAttachments';

const HTTP_OK_MIN = 200;
const HTTP_OK_MAX = 299;
const FILE_FIELD = 'file';

export type XhrFactory = () => XMLHttpRequest;

export function postToPresignedUrl(
  upload: TerminalAttachmentUpload,
  file: File,
  onProgress: (fraction: number) => void,
  createXhr: XhrFactory = () => new XMLHttpRequest(),
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [name, value] of Object.entries(upload.uploadFields)) {
      form.append(name, value);
    }
    form.append(FILE_FIELD, file);
    const xhr = createXhr();
    xhr.open('POST', upload.uploadUrl);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= HTTP_OK_MIN && xhr.status <= HTTP_OK_MAX) {
        onProgress(1);
        resolve();
      } else {
        reject(new Error(`attachment upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('attachment upload failed: network'));
    xhr.onabort = () => reject(new Error('attachment upload aborted'));
    xhr.send(form);
  });
}

export async function uploadAttachment(
  deviceId: string,
  file: File,
  createUpload: (
    deviceId: string,
    file: { name: string; contentType: string; size: number },
  ) => Promise<TerminalAttachmentUpload>,
  onProgress: (fraction: number) => void,
  createXhr?: XhrFactory,
): Promise<TerminalAttachment> {
  const contentType = resolveContentType(file);
  const upload = await createUpload(deviceId, {
    name: file.name,
    contentType,
    size: file.size,
  });
  await postToPresignedUrl(upload, file, onProgress, createXhr);
  return {
    id: upload.attachmentId,
    name: file.name,
    contentType,
    size: file.size,
    downloadUrl: upload.downloadUrl,
  };
}
